import { FC, useState } from 'react';
import { Control, Controller, FieldErrors, useWatch } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  Checkbox,
  FormInputText,
  InputLabel,
  Svg,
  SvgBubble,
  Text,
  TextField,
  Tooltip,
} from '@hopskipdrive/react-components';
import PhoneIcon from '@mui/icons-material/Phone';
import { FormHelperText } from '@mui/material';
import { Button } from '@rideiq/shared/components/Button';

import { ContactGroupFlexRow, ContactGroupFormContainer } from './MustBeMetProtocolForm.styles';
import { formatPhoneNumber, normalizePhoneNumber } from './phoneNumber';
import { Contact, newMustBeMetProtocolFormType } from './types';

interface ContactGroupFormV2Props {
  index: number;
  contacts: Contact[];
  control: Control<newMustBeMetProtocolFormType>;
  errors: FieldErrors<newMustBeMetProtocolFormType>;
  onDelete: (index: number) => void;
}

export const ContactGroupFormV2: FC<ContactGroupFormV2Props> = ({ index, contacts, control, errors, onDelete }) => {
  const contactsWithPhoneNumber = contacts.filter((contact) => !!contact.mobilePhoneNumber);
  const fieldPath = `mustBeMetProtocol.authorizedContactGroups.${index}` as const;
  const groupErrors = errors.mustBeMetProtocol?.authorizedContactGroups?.[index];

  const watchEscalationNumber = useWatch({ control, name: `${fieldPath}.escalationPhoneNumber` });
  const [phoneInputValue, setPhoneInputValue] = useState(() => formatPhoneNumber(watchEscalationNumber || ''));

  return (
    <ContactGroupFormContainer>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Text>Edit authorized individual group</Text>
        <Button color="destructive" onClick={() => onDelete(index)}>
          Delete
        </Button>
      </Box>
      <ContactGroupFlexRow>
        <InputLabel fieldtype="required" htmlFor={`group-name-${index}`}>
          Group name
        </InputLabel>
        <Controller
          name={`${fieldPath}.name`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormInputText
              id={`group-name-${index}`}
              placeholder="Name"
              variant="outlined"
              error={!!groupErrors?.name}
              helperText={groupErrors?.name?.message}
              value={value}
              onChange={onChange}
            />
          )}
        />
        <InputLabel fieldtype="required" htmlFor={`escalation-phone-number-${index}`}>
          Escalation number
        </InputLabel>
        <Box display="flex" alignItems="center" gap="1rem" sx={{ flex: 1 }}>
          <Controller
            name={`${fieldPath}.escalationPhoneNumber`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                freeSolo
                forcePopupIcon={contactsWithPhoneNumber.length > 1}
                value={value || null}
                inputValue={phoneInputValue}
                onInputChange={(_, newValue) => {
                  const rawPhoneNumber = normalizePhoneNumber(newValue);
                  const formattedValue = formatPhoneNumber(rawPhoneNumber);

                  setPhoneInputValue(formattedValue);
                  onChange(rawPhoneNumber);
                }}
                onChange={(_, newValue) => {
                  if (typeof newValue === 'string') {
                    const rawPhoneNumber = normalizePhoneNumber(newValue);
                    const formattedValue = formatPhoneNumber(rawPhoneNumber);

                    setPhoneInputValue(formattedValue);
                    onChange(rawPhoneNumber);
                  } else if (newValue?.mobilePhoneNumber) {
                    const rawPhoneNumber = normalizePhoneNumber(newValue.mobilePhoneNumber);
                    const formattedValue = formatPhoneNumber(rawPhoneNumber);

                    setPhoneInputValue(formattedValue);
                    onChange(rawPhoneNumber);
                  }
                }}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                    ? formatPhoneNumber(option)
                    : `${formatPhoneNumber(option.mobilePhoneNumber)} (${
                        option.firstName && option.lastName
                          ? `${option.firstName} ${option.lastName}`
                          : option.firstName || option.lastName
                      })`
                }
                options={contactsWithPhoneNumber}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    fullWidth
                    id={`escalation-phone-number-${index}`}
                    data-testid={`escalation-phone-number${index}`}
                    placeholder="Select"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <PhoneIcon />,
                    }}
                    error={!!groupErrors?.escalationPhoneNumber}
                    helperText={groupErrors?.escalationPhoneNumber?.message}
                  />
                )}
              />
            )}
          />
          <Tooltip title="CareDrivers will contact this number if they have trouble at drop-off.  Enter the number of someone who will be available and able to assist.">
            <SvgBubble>
              <Svg icon="question" />
            </SvgBubble>
          </Tooltip>
        </Box>
      </ContactGroupFlexRow>
      <Box display="flex" flexDirection="column" gap="1rem">
        <InputLabel fieldtype="required">Verification requirements</InputLabel>
        {groupErrors?.verifyIdBadge && (
          <FormHelperText error={true}>{groupErrors.verifyIdBadge.message}</FormHelperText>
        )}
        <Box display="flex" gap=".5rem" alignItems="center">
          <Box display="flex" alignItems="center" gap=".5rem">
            <Controller
              name={`${fieldPath}.verifyIdBadge`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  id={`verify-id-badge-${index}`}
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                />
              )}
            />
            <InputLabel htmlFor={`verify-id-badge-${index}`}>Show organization ID</InputLabel>
          </Box>
          <Box display="flex" alignItems="center" gap=".5rem">
            <Controller
              name={`${fieldPath}.verifyRiderName`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  id={`verify-rider-name-${index}`}
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                />
              )}
            />
            <InputLabel htmlFor={`verify-rider-name-${index}`}>{'Know  rider’s name'}</InputLabel>
          </Box>
          <Box display="flex" alignItems="center" gap=".5rem">
            <Controller
              name={`${fieldPath}.verifyRiderBirthdate`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  id={`verify-rider-birthdate-${index}`}
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                />
              )}
            />
            <InputLabel htmlFor={`verify-rider-birthdate-${index}`}>{'Know rider’s birthday'}</InputLabel>
          </Box>
        </Box>
      </Box>
    </ContactGroupFormContainer>
  );
};
